import { useEffect, useRef, useState } from 'react'

const THRESHOLD = 70
const MAX_PULL = 130
const RESISTANCE = 0.5

export function usePullToRefresh({ onRefresh, enabled = true }) {
  const [pullY, setPullY] = useState(0)
  const [refreshing, setRefreshing] = useState(false)

  const startY = useRef(null)
  const pullYRef = useRef(0)
  const refreshingRef = useRef(false)
  const onRefreshRef = useRef(onRefresh)

  useEffect(() => { onRefreshRef.current = onRefresh }, [onRefresh])

  useEffect(() => {
    if (!enabled) return

    const handleStart = (e) => {
      if (refreshingRef.current) return
      if (window.scrollY > 0) return
      startY.current = e.touches[0].clientY
      pullYRef.current = 0
    }

    const handleMove = (e) => {
      if (startY.current === null || refreshingRef.current) return
      if (window.scrollY > 0) { startY.current = null; return }
      const delta = e.touches[0].clientY - startY.current
      if (delta <= 0) {
        if (pullYRef.current !== 0) { pullYRef.current = 0; setPullY(0) }
        return
      }
      const eased = Math.min(MAX_PULL, delta * RESISTANCE)
      pullYRef.current = eased
      setPullY(eased)
      if (e.cancelable) e.preventDefault()
    }

    const handleEnd = async () => {
      if (startY.current === null) return
      const finalY = pullYRef.current
      startY.current = null

      if (finalY >= THRESHOLD && !refreshingRef.current) {
        refreshingRef.current = true
        setRefreshing(true)
        setPullY(THRESHOLD)
        try { await onRefreshRef.current?.() } catch {}
        refreshingRef.current = false
        pullYRef.current = 0
        setRefreshing(false)
        setPullY(0)
      } else {
        pullYRef.current = 0
        setPullY(0)
      }
    }

    window.addEventListener('touchstart', handleStart, { passive: true })
    window.addEventListener('touchmove', handleMove, { passive: false })
    window.addEventListener('touchend', handleEnd, { passive: true })
    window.addEventListener('touchcancel', handleEnd, { passive: true })
    return () => {
      window.removeEventListener('touchstart', handleStart)
      window.removeEventListener('touchmove', handleMove)
      window.removeEventListener('touchend', handleEnd)
      window.removeEventListener('touchcancel', handleEnd)
    }
  }, [enabled])

  return { pullY, refreshing, threshold: THRESHOLD }
}
